import { useState, useEffect, useCallback } from 'react';
import { fetchStatements, StatementsParams, StatementsRecord, StatementsResponse } from '../api/salary/statements.service';
import { YearFilter } from '../components/common/YearFilter';

interface UseStatementsResult {
    statements: StatementsRecord | null;
    loading: boolean;
    error: string | null;
    selectedYear: string;
    setSelectedYear: (year: string) => void;
    refetch: () => Promise<void>;
    clearFilters: () => void;
    fetchData: (year?: string) => Promise<void>;
    handleYearChange: (year: string) => void;
}

export const useStatements = (): UseStatementsResult => {
    const [statements, setStatements] = useState<StatementsRecord | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [selectedYear, setSelectedYear] = useState<string>(new Date().getFullYear().toString());

    const fetchData = useCallback(async (
        year: string = selectedYear
    ) => {
        try {
            setLoading(true);
            setError(null);

            const params: StatementsParams = {
                year: year
            };

            const response: StatementsResponse = await fetchStatements(params);
            if (response) {
                setStatements(response.response);
            }
            return response;

        } catch (err) {
            console.error("Failed to fetch statements:", err);
            setError((err as Error).message || "Failed to load statements");
            setStatements(null);
        } finally {
            setLoading(false);
        }
    }, [selectedYear]);

    useEffect(() => {
        fetchData(selectedYear);
    }, [selectedYear, fetchData]);

    const handleYearChange = useCallback((year: string) => {
        setSelectedYear(year);
        fetchData(year);
    }, [fetchData , YearFilter]);

    const clearFilters = useCallback(() => {
        setSelectedYear(new Date().getFullYear().toString());
        fetchData(new Date().getFullYear().toString());
    }, [fetchData]);

    const refetch = useCallback(async () => {
        await fetchData(selectedYear);
    }, [selectedYear, fetchData]);


    return {
        statements,
        loading,
        error,
        selectedYear,
        setSelectedYear,
        refetch,
        clearFilters,
        fetchData,
        handleYearChange,
    };
};